/**
 * Location for a deal address: a MapLibre map with the asset outlined in gold,
 * on a light vector base or on daytime satellite imagery, plus Google Street
 * View when a Maps key is configured.
 *
 * Aerial is the default view (Edward, 29 Oct 2026: aerial by default, light
 * maps). For a street address the parcel or building outline reads best on
 * imagery; for a deal that states only its city, the imagery is the city at
 * city zoom, with no pin and no outline, and says so. It is a raster layer on
 * the same MapLibre map the light view uses, so the gold outline, the pin and
 * the fit are one code path and Aerial makes no Google request and needs no
 * key. A failed or stalled Aerial falls back to the light map with a note; the
 * viewer's own pick always wins. Where the imagery comes from is
 * `aerialImagery.ts`.
 *
 * The map needs a coordinate, and it takes that only from its props: the
 * property master's stored one, or the one the server's site-intel or media
 * lookups resolved for a verified street address. It never geocodes address text
 * itself — a city-only or invented-city address used to be centred and pinned at
 * street zoom here.
 *
 * The outline is the parcel (Regrid) when the platform has a token, and the
 * building footprint from OpenStreetMap otherwise. Both arrive as GeoJSON
 * geometry from `/property-intel`; the map draws whichever it is given and
 * says which. When neither exists it shows the pin alone: a boundary no source
 * supplied would be a guess dressed as a survey. OpenStreetMap's attribution
 * stays on the map — the ODbL requires it — as a compact control and in the
 * outline badge, beside the imagery provider's own.
 *
 * Street View is still Google's; there is no equivalent of it elsewhere. It
 * loads only when shown, so a deal on the map or Aerial makes no Google request
 * for imagery.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import * as maplibregl from 'maplibre-gl'
import type { GeoJSONSource, Map as LibreMap, Marker as LibreMarker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// MapLibre parses tiles on a module worker it locates relative to its own
// script. Vite does not see that dynamic URL, so the built app asked for
// /assets/maplibre-gl-worker.mjs, got index.html, and rendered a black map.
// Bundle the worker explicitly and tell MapLibre where it landed.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { MapPin } from 'lucide-react'
import { countNearby } from '@/services/browserPlaces'
import { loadMapsScript, mapsLibraries, onMapsAuthFailure } from '@/services/googleMapsLoader'
import {
  AERIAL_IMAGERY,
  AERIAL_LAYER_ID,
  AERIAL_LOAD_TIMEOUT_MS,
  AERIAL_SAME_VIEW_M,
  AERIAL_SOURCE_ID,
  AERIAL_UNDERLAY_LAYER_ID,
  AERIAL_UNDERLAY_SOURCE_ID,
  aerialCovers,
  aerialSource,
  aerialUnderlaySource,
  metresApart,
} from './aerialImagery'
import { LIGHT_MAP_STYLE } from './mapStyle'
import { fitPadding } from './mapFit'
import type { GeoJsonGeometry } from '@/services/dossierContext'
import {
  APPROX_SITE_LABEL,
  SHOW_APPROX_SITE_MARKER,
  approxSiteMarker,
  positionsOf,
} from './siteOutline'

type MapMode = 'map' | 'aerial' | 'street'

/** Google's control buttons, in px (its default is 40): sized to the panel's
 *  own controls rather than to a full-screen map. */
const GOOGLE_CONTROL_SIZE = 24

maplibregl.setWorkerUrl(maplibreWorkerUrl)

/** The imagery sources: the slow on-demand one and the fast pre-rendered one beneath it. */
const IMAGERY_SOURCES = new Set([AERIAL_SOURCE_ID, AERIAL_UNDERLAY_SOURCE_ID])
const GOLD = '#f5c451'
/** The outline's line on the light base map: the imagery's bright gold all but
 *  disappears on pale grey. */
const GOLD_ON_LIGHT = '#b8860b'
const NEARBY_RADIUS_M = 800
/** A street address is a building; a city is a region the size of this zoom. */
const SITE_ZOOM = 17
const CITY_ZOOM = 11
/**
 * How long Street View may take to come up before the view gives up on Google.
 * A script request that stalls instead of failing (a proxy, a flaky link)
 * rejects nothing, and the pane would otherwise sit blank until the browser's
 * own timeout.
 */
const GOOGLE_LOAD_TIMEOUT_MS = 12_000

/** What a credit reader counts when sizing a submarket, in reading order. */
const NEARBY_COUNTS: Array<{ label: string; types: string[] }> = [
  { label: 'Transit stops', types: ['subway_station', 'train_station', 'light_rail_station', 'bus_station'] },
  { label: 'Schools', types: ['school'] },
  { label: 'Grocery', types: ['grocery_store', 'supermarket'] },
  { label: 'Retail', types: ['shopping_mall', 'department_store'] },
]

type NeighborhoodStat = {
  label: string
  value: string
  source: string
}

type Coordinate = { lat: number; lng: number }

type Props = {
  address: string
  apiKey: string | null
  keyConfigured: boolean
  keyError?: string | null
  keyLoading?: boolean
  /**
   * Whether aerial imagery may be requested at all (the server's kill switch).
   * Only `true` allows it: off, or not yet known, the imagery host is never
   * contacted and the Aerial view is unavailable.
   */
  aerialEnabled?: boolean | null
  lat?: number | null
  lng?: number | null
  /** The parcel boundary (Regrid), as GeoJSON geometry, when known. */
  parcel?: GeoJsonGeometry | null
  /** The building footprint (OpenStreetMap), drawn when there is no parcel. */
  footprint?: GeoJsonGeometry | null
  /**
   * What the coordinate is a point on. `asset` (default) is a street address;
   * `city` is a locality centre, shown without pin, outline or imagery.
   */
  basis?: 'asset' | 'city'
  /** Why no map may be shown: the address is not verified against a document. */
  blockedReason?: string | null
  /** The caller is still working out where the asset is. */
  locating?: boolean
  /** Fill parent height — used on Overview single-screen layout. */
  compact?: boolean
  /** Parent tile supplies chrome — body only. */
  chromeless?: boolean
  /**
   * Where to draw the Map | Aerial | Street View toggle. Left out, the toggle is the first row of
   * the map body, as it always was (every other consumer and the expanded view keep that). Given an
   * element — the Location panel's header slot — the same toggle is drawn there instead, so the map
   * fills the whole body. `null` is "the slot is not mounted yet": nothing is drawn for that one
   * render, never a second copy. The toggle's state (what is usable, what is picked) stays here:
   * it depends on the map's own coordinate, imagery and Google readiness.
   */
  modeBarSlot?: HTMLElement | null
}

const OUTLINE_SOURCE = 'asset-outline'

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function PropertyMap({
  address,
  apiKey,
  keyConfigured,
  keyError,
  keyLoading,
  aerialEnabled,
  lat,
  lng,
  parcel = null,
  footprint = null,
  basis = 'asset',
  blockedReason = null,
  locating = false,
  compact = false,
  chromeless = false,
  modeBarSlot,
}: Props) {
  const libreEl = useRef<HTMLDivElement | null>(null)
  const streetEl = useRef<HTMLDivElement | null>(null)
  const libreRef = useRef<LibreMap | null>(null)
  const libreMarkerRef = useRef<LibreMarker | null>(null)
  const libreBasisRef = useRef<'asset' | 'city' | null>(null)
  // The current map has drawn at least once (its `load` event has fired).
  const libreLoadedRef = useRef(false)
  // The mode the map's own event handlers act on: they are bound once, when the
  // map is created, and must see the current one.
  const modeRef = useRef<MapMode>('map')
  // Imagery tiles that have actually arrived, and for which place. A map
  // recentred on another deal has painted nothing there yet; one nudged a few
  // metres has, and keeps its count.
  const aerialTilesRef = useRef(0)
  const aerialPlaceRef = useRef<Coordinate | null>(null)
  const streetRef = useRef<google.maps.StreetViewPanorama | null>(null)
  // What the viewer chose. Null until they click a mode: the mode shown is
  // derived from the props below, so a Maps key that arrives after the first
  // paint can still take the map to Aerial, and a click is never overridden.
  const [pick, setPick] = useState<MapMode | null>(null)
  const [aerialFailed, setAerialFailed] = useState(false)
  // The key Google refused for this site, if it has. A refusal is the key's, so
  // it outlives a change of place; any other failure belongs to the place it
  // happened on and is forgotten when the place changes.
  const refusedKeyRef = useRef<string | null>(null)
  // Street View is still on its way up.
  const [googleLoading, setGoogleLoading] = useState(false)
  // The imagery has not painted yet, or could not.
  const [aerialLoading, setAerialLoading] = useState(false)
  const [aerialError, setAerialError] = useState<string | null>(null)
  // What is actually on screen. The badge names an outline, so it is shown only
  // while that outline is drawn on the surface being looked at.
  const [mapOutlined, setMapOutlined] = useState(false)
  // Three surfaces, three failure states: Google's trouble must not blank the
  // map, a base-map tile failure must not paint over imagery that loaded, and
  // an imagery failure falls back to the light map rather than to nothing.
  const [libreError, setLibreError] = useState<string | null>(null)
  const [googleError, setGoogleError] = useState<string | null>(null)
  const [streetUnavailable, setStreetUnavailable] = useState(false)
  const [neighborhood, setNeighborhood] = useState<NeighborhoodStat[]>([])
  const [placesNote, setPlacesNote] = useState<string | null>(null)

  const blocked = Boolean(blockedReason)
  const resolved = useMemo<Coordinate | null>(
    () => (finite(lat) && finite(lng) ? { lat, lng } : null),
    [lat, lng],
  )
  // The coordinate comes only from the props. A place nobody verified gets no
  // site outline either, whatever the caller happens to hold.
  const siteLevel = basis === 'asset' && !blocked
  // A geometry with no coordinates is no outline: it must not take the pin
  // away, be named in the badge, or shadow a footprint that can be drawn.
  const drawable = (shape: GeoJsonGeometry | null): GeoJsonGeometry | null =>
    shape && positionsOf(shape).length > 0 ? shape : null
  const parcelShape = siteLevel ? drawable(parcel) : null
  const parcelOrFootprint = parcelShape ?? (siteLevel ? drawable(footprint) : null)
  const outlineKind: 'parcel' | 'building' | null = parcelOrFootprint
    ? parcelShape
      ? 'parcel'
      : 'building'
    : null
  const wantsApprox = SHOW_APPROX_SITE_MARKER && siteLevel && !parcelOrFootprint
  const approx = useMemo(
    () => (wantsApprox && resolved ? approxSiteMarker(resolved.lat, resolved.lng) : null),
    [wantsApprox, resolved],
  )
  const outline = parcelOrFootprint ?? approx
  const outlineLabel = approx
    ? APPROX_SITE_LABEL
    : outlineKind === 'parcel'
      ? 'Parcel · Regrid'
      : outlineKind === 'building'
        ? 'Building footprint · OpenStreetMap'
        : null
  const hasAddress = Boolean(address.trim())

  const googleReady = Boolean(apiKey)
  // Street View is for a place with a street address: a panorama at a city
  // centre reads as the asset. Aerial is not: at city zoom the imagery is the
  // city, with no pin or outline on it, and the note says it is the city. Aerial
  // needs no key; Street View needs Google's.
  const googleUsable = googleReady && siteLevel
  // The imagery covers the United States; elsewhere it would be a blank tile.
  const aerialCovered = resolved !== null && aerialCovers(resolved.lat, resolved.lng)
  // The imagery host is the browser's to contact, so the server's switch is only
  // honoured if nothing here asks before it has said yes.
  const aerialAllowed = aerialEnabled === true
  const aerialUsable = !blocked && aerialCovered && aerialAllowed
  const mode: MapMode =
    pick && (pick === 'map' || (pick === 'aerial' ? aerialUsable : googleUsable))
      ? pick
      : aerialUsable && !aerialFailed
        ? 'aerial'
        : 'map'
  modeRef.current = mode
  // Everything below that creates a map needs a mounted container.
  const surface = !blocked && resolved !== null

  // A different key is a different chance for Google to say yes (Street View).
  useEffect(() => {
    if (!apiKey) return
    refusedKeyRef.current = null
    setGoogleError(null)
    // Google paints its own error panel in the div and throws nothing when a
    // key is referrer-blocked, so the failure has to be heard from the loader.
    return onMapsAuthFailure(() => {
      refusedKeyRef.current = apiKey
      setGoogleError('Google rejected this Maps key for this site.')
    })
  }, [apiKey])

  // A different place is a different chance too. This component outlives a deal
  // switch, and imagery that failed once on one deal must not leave every later
  // deal on the light map with an "Aerial unavailable" note. A key Google
  // refused stays refused.
  useEffect(() => {
    // A map's own failure belongs to the place it happened on as well: a tile
    // that would not load for one coordinate says nothing about the next.
    setLibreError(null)
    setAerialFailed(false)
    setAerialError(null)
    if (refusedKeyRef.current !== null && refusedKeyRef.current === apiKey) return
    setGoogleError(null)
  }, [resolved, address, apiKey])

  // ── Nearby counts (full-size layout only) ─────────────────────────────
  // The compact map on the Property page hides this block, and Location
  // intelligence already holds the server's counts — four browser Places
  // calls per open for nothing. A city centre has no neighbourhood to count.
  useEffect(() => {
    setNeighborhood((current) => (current.length ? [] : current))
    setPlacesNote(null)
    if (compact || !apiKey || !resolved || !siteLevel) return
    let cancelled = false
    ;(async () => {
      try {
        await loadMapsScript(apiKey)
        await mapsLibraries()
        if (cancelled) return
        const center = new google.maps.LatLng(resolved.lat, resolved.lng)
        // `transit_station` is a Table B type in Places API (New): it can come
        // back on a result but cannot be searched on, so the station kinds are
        // named individually rather than silently returning nothing.
        const counts = await Promise.all(
          NEARBY_COUNTS.map((entry) => countNearby(center, entry.types, NEARBY_RADIUS_M)),
        )
        if (cancelled) return
        if (counts.every((count) => count === null)) {
          setPlacesNote('Nearby place counts unavailable — enable “Places API (New)” on this Google key.')
          return
        }
        setNeighborhood(
          counts.flatMap((count, index) =>
            count === null
              ? []
              : [{
                  label: `${NEARBY_COUNTS[index].label} (${NEARBY_RADIUS_M}m)`,
                  value: String(count),
                  source: 'Google Places nearby search',
                }],
          ),
        )
      } catch {
        // The counts are a nicety; the map does not depend on them.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiKey, compact, resolved, siteLevel])

  // ── The map: MapLibre, on OpenFreeMap light or on satellite imagery ────
  const hasOutline = Boolean(outline)
  useEffect(() => {
    if (!surface || mode === 'street' || !resolved || !libreEl.current) return
    const center: [number, number] = [resolved.lng, resolved.lat]
    const zoom = basis === 'city' ? CITY_ZOOM : SITE_ZOOM
    let map: LibreMap | null = libreRef.current
    if (!map) {
      try {
        map = new maplibregl.Map({
          container: libreEl.current,
          style: LIGHT_MAP_STYLE,
          center,
          zoom,
          attributionControl: { compact: true },
          // No keyboard-shortcuts overlay, no rotation gesture surprises.
          keyboard: false,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
        })
      } catch (err) {
        setLibreError(err instanceof Error ? err.message : 'The map failed to start.')
        return
      }
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
      // The compact attribution opens itself on first load and stays open
      // until the map is touched — a text box over a quarter of a panel-sized
      // map. Fold it to its ⓘ once the first frame is up; it opens on click.
      map.once('idle', () => {
        libreEl.current
          ?.querySelector('.maplibregl-ctrl-attrib.maplibregl-compact-show')
          ?.classList.remove('maplibregl-compact-show')
      })
      map.on('error', (event) => {
        // The imagery's own tiles are heard from the Aerial readiness check
        // below: a missing tile at the edge of coverage is a gap, not a failure.
        if (IMAGERY_SOURCES.has((event as { sourceId?: string }).sourceId ?? '')) return
        // A style or tile that would not load. Said once, in place of a
        // silent black square. Once the map has drawn, one tile or glyph that
        // fails is a gap in the base map, not a reason to cover the rest of it.
        if (libreLoadedRef.current) return
        const message = event?.error?.message
        if (message && /style|tile|fetch|network/i.test(message)) {
          setLibreError('The map tiles could not be loaded.')
        }
      })
      // The map came up (`load`) or caught up with everything it was asked for
      // (`idle`): whatever failed before it is behind it.
      map.on('load', () => {
        libreLoadedRef.current = true
        setLibreError(null)
      })
      map.on('idle', () => setLibreError(null))
      // Imagery that has actually arrived, as opposed to imagery that was asked for.
      map.on('sourcedata', (event) => {
        const arrived = event as { sourceId?: string; tile?: unknown }
        if (IMAGERY_SOURCES.has(arrived.sourceId ?? '') && arrived.tile) aerialTilesRef.current += 1
      })
      libreRef.current = map
      libreBasisRef.current = basis
    } else {
      map.setCenter(center)
      if (libreBasisRef.current !== basis) {
        map.setZoom(zoom)
        libreBasisRef.current = basis
      }
    }
    const live: LibreMap = map
    // A pin is a claim about a site; a city centre makes no such claim. On
    // Aerial it would sit on the parcel it is meant to explain, so it goes when
    // an outline is drawn in its place.
    if (basis !== 'asset' || (mode === 'aerial' && hasOutline)) {
      libreMarkerRef.current?.remove()
      libreMarkerRef.current = null
    } else if (!libreMarkerRef.current) {
      libreMarkerRef.current = new maplibregl.Marker({ color: GOLD }).setLngLat(center).addTo(live)
    } else {
      libreMarkerRef.current.setLngLat(center)
    }
    // A hidden container (the mode was Aerial when the map was created, or
    // the panel just expanded) leaves the canvas at the wrong size.
    live.resize()
  }, [surface, mode, resolved, basis, hasOutline])

  // The imagery layer rides on the light style, hidden until Aerial is shown. It
  // is added only once the server has allowed aerial imagery, so a switched-off
  // or not-yet-known switch never puts the imagery host in the style at all. It
  // goes under the gold outline whenever that exists already.
  useEffect(() => {
    const map = libreRef.current
    if (!surface || !map || !aerialAllowed) return
    const addAerial = () => {
      if (map.getSource(AERIAL_SOURCE_ID)) return
      const before = map.getLayer(`${OUTLINE_SOURCE}-fill`) ? `${OUTLINE_SOURCE}-fill` : undefined
      const visibility = modeRef.current === 'aerial' ? 'visible' : 'none'
      const underlay = aerialUnderlaySource(AERIAL_IMAGERY)
      try {
        // The pre-rendered underlay first, so the on-demand imagery draws over it
        // where it has arrived and the underlay shows wherever it has not.
        if (underlay) {
          map.addSource(AERIAL_UNDERLAY_SOURCE_ID, underlay)
          map.addLayer({ id: AERIAL_UNDERLAY_LAYER_ID, type: 'raster', source: AERIAL_UNDERLAY_SOURCE_ID, layout: { visibility } }, before)
        }
        map.addSource(AERIAL_SOURCE_ID, aerialSource(AERIAL_IMAGERY))
        map.addLayer(
          {
            id: AERIAL_LAYER_ID,
            type: 'raster',
            source: AERIAL_SOURCE_ID,
            // Below the underlay's last zoom the underlay is the same imagery, at once.
            ...(AERIAL_IMAGERY.underlay ? { minzoom: AERIAL_IMAGERY.underlay.minzoom } : {}),
            layout: { visibility },
          },
          before,
        )
      } catch {
        // The style went away under us: the map keeps its light base, and the
        // readiness check below reports that no imagery arrived.
      }
    }
    if (map.getStyle()) {
      addAerial()
      return
    }
    map.once('style.load', addAerial)
    return () => {
      map.off('style.load', addAerial)
    }
  }, [surface, mode, resolved, aerialAllowed])

  // The gold outline, redrawn whenever it changes. A source can only be added
  // once the style JSON is in (`getStyle()` answers nothing before that); it
  // does NOT have to wait for tiles, which is what `isStyleLoaded()` asks and
  // what left a draw registered on a `load` event that had already come and
  // gone. So: draw now if the style is in, otherwise on its `style.load`.
  useEffect(() => {
    const map = libreRef.current
    setMapOutlined(false)
    if (!surface || !map || mode === 'street') return
    const draw = () => {
      const existing = map.getSource(OUTLINE_SOURCE) as GeoJSONSource | undefined
      const data = outline
        ? { type: 'Feature' as const, properties: {}, geometry: outline as never }
        : { type: 'FeatureCollection' as const, features: [] }
      try {
        if (existing) {
          existing.setData(data as never)
        } else {
          map.addSource(OUTLINE_SOURCE, { type: 'geojson', data: data as never })
          map.addLayer({
            id: `${OUTLINE_SOURCE}-fill`,
            type: 'fill',
            source: OUTLINE_SOURCE,
            paint: { 'fill-color': GOLD, 'fill-opacity': 0.1 },
          })
          map.addLayer({
            id: `${OUTLINE_SOURCE}-line`,
            type: 'line',
            source: OUTLINE_SOURCE,
            paint: { 'line-color': GOLD, 'line-width': 2.5, 'line-opacity': 0.95 },
          })
          // Imagery added first stays under the outline it is there to show off.
          if (map.getLayer(AERIAL_UNDERLAY_LAYER_ID)) map.moveLayer(AERIAL_UNDERLAY_LAYER_ID, `${OUTLINE_SOURCE}-fill`)
          if (map.getLayer(AERIAL_LAYER_ID)) map.moveLayer(AERIAL_LAYER_ID, `${OUTLINE_SOURCE}-fill`)
        }
      } catch {
        // The style went away under us; the outline is not on the map, and the
        // badge, which follows `mapOutlined`, does not claim it is.
        return
      }
      if (outline) {
        const positions = positionsOf(outline)
        const bounds = positions.reduce(
          (b, p) => b.extend(p),
          new maplibregl.LngLatBounds(positions[0], positions[0]),
        )
        map.fitBounds(bounds, { padding: fitPadding(libreEl.current), maxZoom: 19, duration: 0 })
      }
      setMapOutlined(Boolean(outline))
    }
    if (map.getStyle()) {
      draw()
      return
    }
    map.once('style.load', draw)
    return () => {
      map.off('style.load', draw)
    }
  }, [surface, outline, mode, resolved])

  // Aerial is the imagery layer shown; the map is the same layer hidden.
  useEffect(() => {
    const map = libreRef.current
    if (!surface || !map || !map.getLayer(AERIAL_LAYER_ID)) return
    map.setLayoutProperty(AERIAL_LAYER_ID, 'visibility', mode === 'aerial' ? 'visible' : 'none')
    if (map.getLayer(AERIAL_UNDERLAY_LAYER_ID)) {
      map.setLayoutProperty(AERIAL_UNDERLAY_LAYER_ID, 'visibility', mode === 'aerial' ? 'visible' : 'none')
    }
  }, [surface, mode])

  // The outline's line: bright gold on imagery, a deeper gold on the light map.
  useEffect(() => {
    const map = libreRef.current
    if (!surface || !map || !mapOutlined || typeof map.setPaintProperty !== 'function') return
    if (!map.getLayer(`${OUTLINE_SOURCE}-line`)) return
    map.setPaintProperty(`${OUTLINE_SOURCE}-line`, 'line-color', mode === 'aerial' ? GOLD : GOLD_ON_LIGHT)
  }, [surface, mode, mapOutlined])

  // Aerial is up only once imagery has arrived. Constructing the layer is not
  // having imagery: tile requests can stall or be refused, and the pane would
  // then stay blank with nothing left to time out. The first tile to arrive ends
  // the wait, so the picture fills in as it loads rather than after all of it.
  // The map reports `idle` when it has nothing more to load; no tile having
  // arrived by then, or none within the time allowed, is a failure the view
  // falls back from.
  useEffect(() => {
    const map = libreRef.current
    if (!surface || !map || mode !== 'aerial' || !resolved) {
      setAerialLoading(false)
      return
    }
    // Imagery already painted for this place is not waited for a second time. "This place" is the
    // view the imagery arrived for, not a rounded coordinate: a refined coordinate for the same
    // address lands on the tiles already loaded, no new tile arrives, and waiting for one would
    // call working imagery unavailable. The anchor stays where the imagery was confirmed, so a run
    // of small moves cannot walk it away.
    const anchor = aerialPlaceRef.current
    if (anchor === null || metresApart(anchor, resolved) > AERIAL_SAME_VIEW_M) {
      aerialPlaceRef.current = { lat: resolved.lat, lng: resolved.lng }
      aerialTilesRef.current = 0
    }
    let finished = false
    const onIdle = () => {
      if (finished) return
      if (aerialTilesRef.current === 0) fail('Aerial imagery could not be loaded.')
      else done()
    }
    const onTile = (event: unknown) => {
      const arrived = event as { sourceId?: string; tile?: unknown }
      if (IMAGERY_SOURCES.has(arrived.sourceId ?? '') && arrived.tile && !finished) done()
    }
    const done = () => {
      finished = true
      clearTimeout(timer)
      map.off('idle', onIdle)
      map.off('sourcedata', onTile)
      setAerialLoading(false)
    }
    const fail = (message: string) => {
      if (finished) return
      done()
      setAerialError(message)
      // The default view failed: let the derived mode settle on the light map.
      setAerialFailed(true)
    }
    const timer = setTimeout(() => fail('Aerial imagery did not respond in time.'), AERIAL_LOAD_TIMEOUT_MS)
    setAerialError(null)
    if (aerialTilesRef.current > 0 && map.isStyleLoaded()) {
      done()
    } else {
      setAerialLoading(true)
      map.on('idle', onIdle)
      map.on('sourcedata', onTile)
    }
    return () => {
      finished = true
      clearTimeout(timer)
      map.off('idle', onIdle)
      map.off('sourcedata', onTile)
    }
  }, [surface, mode, resolved])

  // The containers unmount with the surface (a coordinate withdrawn, the
  // address blocked); what was built on them goes too.
  useEffect(() => {
    if (surface) return
    libreMarkerRef.current?.remove()
    libreMarkerRef.current = null
    libreRef.current?.remove()
    libreRef.current = null
    libreBasisRef.current = null
    libreLoadedRef.current = false
    aerialTilesRef.current = 0
    aerialPlaceRef.current = null
    streetRef.current = null
  }, [surface])

  useEffect(
    () => () => {
      libreMarkerRef.current?.remove()
      libreMarkerRef.current = null
      libreRef.current?.remove()
      libreRef.current = null
    },
    [],
  )

  // ── Google: Street View ───────────────────────────────────────────────
  useEffect(() => {
    if (!surface || mode !== 'street' || !apiKey || !resolved || !googleUsable) return
    let stale = false
    // The panorama is up, or this attempt has been given up on.
    let finished = false
    setStreetUnavailable(false)
    setGoogleError(null)
    setGoogleLoading(streetRef.current === null)

    const finish = () => {
      finished = true
      clearTimeout(timer)
      setGoogleLoading(false)
    }
    const fail = (message: string) => {
      if (stale || finished) return
      finish()
      setGoogleError(message)
    }
    // A request that stalls rejects nothing; without this the pane stays blank.
    const timer = setTimeout(() => fail('Google Maps did not respond in time.'), GOOGLE_LOAD_TIMEOUT_MS)

    ;(async () => {
      try {
        await loadMapsScript(apiKey)
        const libs = await mapsLibraries()
        if (stale || finished) return
        const location = new google.maps.LatLng(resolved.lat, resolved.lng)
        if (!libs.StreetViewService || !streetEl.current) {
          setStreetUnavailable(true)
          finish()
          return
        }
        const sv = new libs.StreetViewService()
        sv.getPanorama({ location, radius: 80 }, (data, status) => {
          if (stale) return
          finish()
          if (status !== libs.StreetViewStatus.OK || !data?.location?.latLng || !streetEl.current) {
            setStreetUnavailable(true)
            return
          }
          const pano =
            streetRef.current ??
            new libs.StreetViewPanorama(streetEl.current, {
              disableDefaultUI: true,
              controlSize: GOOGLE_CONTROL_SIZE,
              addressControl: false,
              linksControl: true,
              panControl: true,
              enableCloseButton: false,
            })
          pano.setPano(data.location.pano)
          pano.setVisible(true)
          streetRef.current = pano
          setStreetUnavailable(false)
        })
      } catch (err) {
        fail(err instanceof Error ? err.message : 'Google Maps failed to load.')
      }
    })()
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [surface, mode, apiKey, resolved, googleUsable])

  const modes: { id: MapMode; label: string; needsKey: boolean }[] = [
    { id: 'map', label: 'Map', needsKey: false },
    { id: 'aerial', label: 'Aerial', needsKey: false },
    { id: 'street', label: 'Street View', needsKey: true },
  ]

  const mapMinH = compact ? 'min-h-0' : 'min-h-[220px]'

  // Drawn in the parent's header row (the Location panel), not as the map body's first row.
  const inHeader = modeBarSlot !== undefined

  const modeBar = (
    <div
      role={inHeader ? 'group' : undefined}
      aria-label={inHeader ? 'Map view' : undefined}
      className={
        inHeader
          ? 'flex shrink-0 items-center gap-1'
          : `flex shrink-0 items-center justify-end gap-1 ${chromeless ? 'h-7 border-b border-amber-400/10 px-1' : 'border-b border-amber-400/10 px-3 py-2'}`
      }
    >
      {!chromeless && (
        <div className="mr-auto flex items-center gap-1.5">
          <MapPin size={11} className="text-amber-300" />
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">Location</span>
        </div>
      )}
      {modes.map((m) => {
        // Street View is for a place with a street address; the map and Aerial
        // show a city too.
        const siteOnly = m.id === 'street'
        const outOfCoverage = m.id === 'aerial' && !blocked && resolved !== null && !aerialCovered
        const switchedOff = m.id === 'aerial' && !blocked && (resolved === null || aerialCovered) && !aerialAllowed
        // Imagery needs a point to be imagery of; the switch's own reason still
        // comes first when it is off.
        const noPoint = m.id === 'aerial' && !blocked && resolved === null && aerialAllowed
        const disabled = blocked || (siteOnly && !siteLevel) || noPoint || outOfCoverage || switchedOff || (m.needsKey && !googleReady)
        const why = blocked
          ? blockedReason ?? undefined
          : siteOnly && !siteLevel
            ? 'Needs a street address'
            : noPoint
              ? 'No coordinate on record yet'
              : outOfCoverage
              ? 'Aerial imagery covers the United States only'
              : switchedOff
                ? aerialEnabled === false
                  ? 'Aerial imagery is switched off'
                  : keyLoading
                    ? 'Loading map settings…'
                    : 'Aerial imagery is not available'
              : m.needsKey && !googleReady
              ? keyLoading
                ? 'Loading map settings…'
                : `${m.label} needs a Google Maps key in Settings`
              : undefined
        return (
          <button
            key={m.id}
            type="button"
            data-testid={`property-map-mode-${m.id}`}
            data-active={mode === m.id ? 'true' : 'false'}
            onClick={() => !disabled && setPick(m.id)}
            disabled={disabled}
            title={disabled ? why : undefined}
            className={`rounded px-1.5 py-0.5 text-[9px] ${
              mode === m.id
                ? 'bg-amber-400/15 text-amber-200'
                : disabled
                  ? 'cursor-not-allowed text-stone-700'
                  : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            {m.label}
          </button>
        )
      })}
    </div>
  )

  const message = (text: string, testId: string) => (
    <p
      data-testid={testId}
      className={`flex h-full ${mapMinH} items-center justify-center px-6 text-center text-[12px] text-stone-500`}
    >
      {text}
    </p>
  )

  const overlay = (text: string, tone: string, testId: string) => (
    <div
      data-testid={testId}
      className={`absolute inset-0 flex items-center justify-center bg-[#090909] px-6 text-center text-[12px] ${tone}`}
    >
      {text}
    </div>
  )

  // Named only while that outline is drawn on the surface being looked at: the
  // Street View panorama carries none, and Aerial has none until its imagery is up.
  const outlineBadgeVisible =
    Boolean(outlineLabel) &&
    resolved !== null &&
    (mode === 'map'
      ? mapOutlined && !libreError
      : mode === 'aerial'
        ? mapOutlined && !libreError && !aerialError
        : false)

  const mapBody = (
    <div className={`relative flex-1 ${mapMinH}`}>
      {blocked && (
        <div
          data-testid="property-map-blocked"
          className={`flex h-full ${mapMinH} flex-col items-center justify-center gap-1 px-6 text-center`}
        >
          <p className="text-[12px] text-stone-400">
            Address not verified against a document — no map is shown.
          </p>
          <p className="text-[10px] text-stone-600">{blockedReason}</p>
        </div>
      )}
      {!blocked && !resolved && locating && message('Locating…', 'property-map-locating')}
      {!blocked && !resolved && !locating && !hasAddress &&
        message('No address on record — no map to show.', 'property-map-no-address')}
      {!blocked && !resolved && !locating && hasAddress && (
        <div
          data-testid="property-map-no-coordinate"
          className={`flex h-full ${mapMinH} flex-col items-center justify-center gap-1 px-6 text-center`}
        >
          <p className="text-[12px] text-stone-400">No coordinate on record for this address yet.</p>
          {keyError && <p className="text-[10px] text-red-300/80">{keyError}</p>}
          {!keyError && !keyConfigured && (
            <p className="text-[10px] text-stone-600">
              Street View needs a Google Maps key in Settings.
            </p>
          )}
        </div>
      )}
      {surface && (
        <>
          <div
            ref={libreEl}
            className={`absolute inset-0 ${mode === 'street' ? 'hidden' : ''}`}
            aria-label={mode === 'aerial' ? 'Aerial view' : 'Property map'}
            data-testid="property-map-libre"
            data-view={mode}
          />
          <div
            ref={streetEl}
            className={`absolute inset-0 ${mode === 'street' ? '' : 'hidden'}`}
            aria-label="Street View"
            data-testid="property-map-street"
          />
          {outlineBadgeVisible && (
            <span
              data-testid="property-map-outline-badge"
              className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-200"
            >
              {outlineLabel}
            </span>
          )}
          {basis === 'city' && mode !== 'street' && !libreError && !(mode === 'aerial' && aerialError) && (
            <span
              data-testid="property-map-city-note"
              className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-stone-300"
            >
              City level · area view, not the site
            </span>
          )}
          {mode === 'map' && aerialFailed && pick === null && !libreError && (
            <span
              data-testid="property-map-aerial-note"
              className="pointer-events-none absolute right-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[9px] text-stone-400"
            >
              Aerial unavailable
            </span>
          )}
          {mode !== 'street' && libreError && overlay(libreError, 'text-red-300', 'property-map-libre-error')}
          {mode === 'aerial' && aerialError && !libreError &&
            overlay(aerialError, 'text-red-300', 'property-map-aerial-error')}
          {mode === 'aerial' && aerialLoading && !aerialError && !libreError &&
            overlay('Loading satellite view…', 'text-stone-500', 'property-map-aerial-loading')}
          {mode === 'street' && googleError && overlay(googleError, 'text-red-300', 'property-map-google-error')}
          {mode === 'street' && googleLoading && !googleError &&
            overlay('Loading Street View…', 'text-stone-500', 'property-map-google-loading')}
          {mode === 'street' && streetUnavailable && !googleError &&
            overlay('Street View unavailable.', 'text-stone-400', 'property-map-street-unavailable')}
        </>
      )}
    </div>
  )

  // The toggle where the caller asked for it: inline (the default), in the caller's slot, or — while that
  // slot is not mounted yet — nowhere.
  const bar = inHeader ? (modeBarSlot ? createPortal(modeBar, modeBarSlot) : null) : modeBar

  if (chromeless) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {bar}
        {mapBody}
      </div>
    )
  }

  return (
    <section
      className={`flex flex-col overflow-hidden rounded-lg border border-amber-400/15 bg-black/30 ${
        compact ? 'h-full min-h-0' : 'min-h-[280px]'
      }`}
    >
      {bar}
      {mapBody}
      {!compact && (neighborhood.length > 0 || placesNote) && (
        <div className="grid grid-cols-2 gap-2 border-t border-amber-400/10 px-3 py-2">
          {neighborhood.map((stat) => (
            <div key={stat.label}>
              <div className="font-mono text-[9px] uppercase tracking-widest text-stone-600">{stat.label}</div>
              <div className="text-[12px] text-stone-200">{stat.value}</div>
              <div className="text-[9px] text-stone-600">{stat.source}</div>
            </div>
          ))}
          {placesNote && <p className="col-span-2 text-[10px] text-stone-500">{placesNote}</p>}
        </div>
      )}
    </section>
  )
}

export default PropertyMap
